import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, Heart, ExternalLink, Plus, Loader, Globe, AlertCircle, Check, Clipboard, X, RefreshCw } from 'lucide-react';
import { recipes as recipesApi, user as userApi } from '../utils/api';
import { saveRecipe, convertConversationToRecipe } from '../utils/recipeHelpers';
import { toast } from 'sonner';

// Default cooking sites
const DEFAULT_SITES = [
  { name: 'AllRecipes', url: 'https://www.allrecipes.com', icon: '🍳' },
  { name: 'Food Network', url: 'https://www.foodnetwork.com/recipes', icon: '📺' },
  { name: 'Epicurious', url: 'https://www.epicurious.com/recipes-menus', icon: '🍴' },
  { name: 'Bon Appétit', url: 'https://www.bonappetit.com/recipes', icon: '👨‍🍳' },
  { name: 'Serious Eats', url: 'https://www.seriouseats.com/recipes', icon: '🔬' },
  { name: 'Simply Recipes', url: 'https://www.simplyrecipes.com', icon: '🥗' },
  { name: 'Taste of Home', url: 'https://www.tasteofhome.com/recipes', icon: '🏠' },
  { name: 'Delish', url: 'https://www.delish.com/cooking/recipes', icon: '😋' },
  { name: 'Budget Bytes', url: 'https://www.budgetbytes.com', icon: '💰' },
  { name: 'Minimalist Baker', url: 'https://minimalistbaker.com/recipes', icon: '🌱' },
  { name: 'Smitten Kitchen', url: 'https://smittenkitchen.com', icon: '🍰' },
  { name: 'NYT Cooking', url: 'https://cooking.nytimes.com', icon: '📰' },
  { name: 'TikTok Recipes', url: 'tiktok', icon: '🎵', isSocial: true },
  { name: 'Instagram Recipes', url: 'instagram', icon: '📸', isSocial: true },
];

const BrowseRecipes = () => {
  const navigate = useNavigate();
  
  const [urlInput, setUrlInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [importing, setImporting] = useState(false);
  const [importSuccess, setImportSuccess] = useState(null);
  const [duplicateRecipe, setDuplicateRecipe] = useState(null);
  const [reimporting, setReimporting] = useState(false);
  const [waitingForReturn, setWaitingForReturn] = useState(false);
  const [openedSite, setOpenedSite] = useState(null);
  
  // Custom sites from database
  const [customSites, setCustomSites] = useState([]);
  const [showAddSiteModal, setShowAddSiteModal] = useState(false);
  const [newSiteName, setNewSiteName] = useState('');
  const [newSiteUrl, setNewSiteUrl] = useState('');
  const [addingSite, setAddingSite] = useState(false);
  
  // Favorites stored in localStorage
  const [favorites, setFavorites] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('recipeSiteFavorites') || '[]');
    } catch {
      return [];
    }
  });

  // Load custom sites from database
  useEffect(() => {
    const loadCustomSites = async () => {
      try {
        const response = await userApi.getCustomSites();
        setCustomSites(response.data.sites || []);
      } catch (err) {
        console.error('Failed to load custom sites:', err);
      }
    };
    loadCustomSites();
  }, []);

  // Save favorites to localStorage
  useEffect(() => {
    localStorage.setItem('recipeSiteFavorites', JSON.stringify(favorites));
  }, [favorites]);

  // Listen for when user returns to this tab
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && waitingForReturn) {
        // User returned to Honey tab - focus the URL input
        const input = document.getElementById('url-input');
        if (input) {
          input.focus();
        }
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [waitingForReturn]);

  const toggleFavorite = (siteUrl, e) => {
    if (e) e.stopPropagation();
    setFavorites(prev => 
      prev.includes(siteUrl) 
        ? prev.filter(u => u !== siteUrl)
        : [...prev, siteUrl]
    );
  };

  const handleAddCustomSite = async () => {
    if (!newSiteName.trim() || !newSiteUrl.trim()) return;
    
    setAddingSite(true);
    try {
      // Ensure URL has protocol
      let url = newSiteUrl.trim();
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        url = 'https://' + url;
      }
      
      const response = await userApi.addCustomSite({ name: newSiteName.trim(), url });
      setCustomSites(prev => [...prev, response.data.site]);
      setShowAddSiteModal(false);
      setNewSiteName('');
      setNewSiteUrl('');
      toast.success('Site added!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add site');
    } finally {
      setAddingSite(false);
    }
  };

  const handleDeleteCustomSite = async (url, e) => {
    e.stopPropagation();
    try {
      await userApi.deleteCustomSite(url);
      setCustomSites(prev => prev.filter(s => s.url !== url));
      toast.success('Site removed');
    } catch (err) {
      toast.error('Failed to remove site');
    }
  };

  const checkForDuplicate = async (url) => {
    try {
      const response = await recipesApi.checkRecipeExists(url);
      if (response.data.exists) {
        setDuplicateRecipe({ ...response.data, source_url: url });
        return true;
      }
    } catch (err) {
      // Ignore errors
    }
    return false;
  };

  const handleReimport = async () => {
    if (!duplicateRecipe?.recipe_id || !duplicateRecipe?.source_url) return;
    setReimporting(true);
    setError('');
    try {
      // Step 1: Same import path as normal — scrape + AI extract
      const importResponse = await recipesApi.importFromUrl(duplicateRecipe.source_url);

      if (!importResponse.data.success || !importResponse.data.recipe_text) {
        toast.error('Could not extract recipe from URL.');
        return;
      }

      // Step 2: Same conversion path as normal import
      const recipeText = importResponse.data.recipe_text;
      const sourceImage = importResponse.data.source_image || null;

      const conversionResult = await convertConversationToRecipe([
        { role: 'assistant', content: recipeText }
      ]);

      if (!conversionResult.success || !conversionResult.recipe) {
        toast.error('Could not parse recipe structure.');
        return;
      }

      // Step 3: Instead of saveRecipe, UPDATE existing recipe — replace content, keep personal data
      const updateData = {
        ingredients: conversionResult.recipe.ingredients || [],
        instructions: conversionResult.recipe.instructions || [],
        description: conversionResult.recipe.description || undefined,
        prep_time: importResponse.data.prep_time || conversionResult.recipe.prep_time,
        cook_time: importResponse.data.cook_time || conversionResult.recipe.cook_time,
        servings: importResponse.data.servings || conversionResult.recipe.servings,
        nutrition: importResponse.data.nutrition || conversionResult.recipe.nutrition,
      };

      if (sourceImage) {
        updateData.source_image = sourceImage;
      }

      await recipesApi.update(duplicateRecipe.recipe_id, updateData);

      toast.success('Recipe re-imported with fresh data');
      setDuplicateRecipe(null);
      navigate(`/recipe/${duplicateRecipe.recipe_id}`);
    } catch (err) {
      console.error('Re-import failed:', err);
      toast.error(err.response?.data?.detail || 'Failed to re-import recipe');
    } finally {
      setReimporting(false);
    }
  };

  const handleImportUrl = async (e) => {
    e?.preventDefault();
    if (!urlInput.trim()) return;
    
    const url = urlInput.trim();
    setLoading(true);
    setError('');
    setImportSuccess(null);
    setDuplicateRecipe(null);
    
    // Check for duplicate first
    const isDuplicate = await checkForDuplicate(url);
    if (isDuplicate) {
      setLoading(false);
      setUrlInput(''); // Clear input - no need to keep a URL we already have
      return;
    }
    
    try {
      setImporting(true);
      
      // Step 1: Import and extract recipe from URL
      const importResponse = await recipesApi.importFromUrl(url);
      
      if (!importResponse.data.success || !importResponse.data.recipe_text) {
        setError('Could not extract recipe from this URL. Make sure it\'s a direct link to a recipe page.');
        return;
      }
      
      // Step 2: Convert the recipe text to structured format
      const recipeText = importResponse.data.recipe_text;
      const sourceUrl = importResponse.data.source_url || url;
      const sourceImage = importResponse.data.source_image || null;
      console.log('[URL Import] Full API response keys:', Object.keys(importResponse.data));
      console.log('[URL Import] source_image from backend:', sourceImage);
      console.log('[URL Import] source_image type:', typeof sourceImage);
      
      const conversionResult = await convertConversationToRecipe([
        { role: 'assistant', content: recipeText }
      ]);
      
      if (!conversionResult.success || !conversionResult.recipe) {
        setError('Could not parse recipe structure. Try a different recipe page.');
        return;
      }
      
      // Merge backend-extracted metadata (prep_time, cook_time, servings, nutrition) 
      // with the converted recipe - backend values take precedence as they're from structured data
      const enrichedRecipe = {
        ...conversionResult.recipe,
        prep_time: importResponse.data.prep_time || conversionResult.recipe.prep_time,
        cook_time: importResponse.data.cook_time || conversionResult.recipe.cook_time,
        servings: importResponse.data.servings || conversionResult.recipe.servings,
        nutrition: importResponse.data.nutrition || conversionResult.recipe.nutrition,
        nutrition_source: importResponse.data.nutrition_source || 'scraped',
      };
      
      // Step 3: Save the recipe (including source image)
      const saveResult = await saveRecipe(enrichedRecipe, 'url_import', sourceUrl, [], sourceImage);
      
      if (saveResult.success && saveResult.recipeId) {
        setImportSuccess({
          recipeId: saveResult.recipeId,
          title: conversionResult.recipe.title || importResponse.data.title || 'Recipe'
        });
        setUrlInput('');
        setWaitingForReturn(false);
        setOpenedSite(null);
      } else {
        setError('Failed to save recipe. Please try again.');
      }
    } catch (err) {
      console.error('Failed to import recipe:', err);
      const errorDetail = err.response?.data?.detail || '';
      
      if (errorDetail.includes('Could not find a valid recipe') || errorDetail.includes('No recipe found')) {
        setError('No recipe found at this URL. Please paste a link to a specific recipe page (not a search or category page).');
      } else {
        setError(errorDetail || 'Failed to import recipe. Please check the URL and try again.');
      }
      // Clear the URL input so user can easily try a different one
      setUrlInput('');
      // Auto-dismiss error after 8 seconds
      setTimeout(() => setError(''), 8000);
    } finally {
      setLoading(false);
      setImporting(false);
    }
  };

  const handleGoogleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      const googleUrl = `https://www.google.com/search?q=${encodeURIComponent(searchQuery + ' recipe')}`;
      window.open(googleUrl, '_blank');
      setWaitingForReturn(true);
      setOpenedSite({ name: 'Google Search', url: googleUrl });
    }
  };

  const [showSocialTip, setShowSocialTip] = useState(false);

  const handleSiteClick = (site) => {
    if (site.isSocial) {
      setShowSocialTip(true);
      setTimeout(() => setShowSocialTip(false), 6000);
      return;
    }
    window.open(site.url, '_blank');
    setWaitingForReturn(true);
    setOpenedSite(site);
  };

  const handlePasteFromClipboard = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text && text.startsWith('http')) {
        setUrlInput(text);
      }
    } catch (err) {
      // Clipboard access denied - user will need to paste manually
    }
  };

  // Separate sites into favorites and others
  const favoriteSites = DEFAULT_SITES.filter(site => !site.isSocial && favorites.includes(site.url));
  const otherSites = DEFAULT_SITES.filter(site => !site.isSocial && !favorites.includes(site.url));
  const socialSites = DEFAULT_SITES.filter(site => site.isSocial);

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm shadow-sm sticky top-0 z-40">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/recipe-box')}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Import Recipe</h1>
              <p className="text-sm text-gray-500">Browse sites and paste recipe URLs</p>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        
        {/* Waiting for Return Banner */}
        {waitingForReturn && openedSite && (
          <div className="bg-gradient-to-r from-amber-100 to-orange-100 border border-amber-200 rounded-2xl p-5 animate-pulse">
            <div className="flex items-center gap-4">
              <div className="text-4xl">{openedSite.icon || '🌐'}</div>
              <div className="flex-1">
                <p className="font-semibold text-amber-900">Browsing {openedSite.name}...</p>
                <p className="text-sm text-amber-700 mt-1">
                  Find a recipe you like, copy its URL, then come back here and paste it below!
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Success Message */}
        {importSuccess && (
          <div className="bg-green-50 border border-green-200 rounded-2xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <Check size={20} className="text-green-600" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-green-800">Recipe imported!</p>
                <p className="text-sm text-green-600">{importSuccess.title}</p>
              </div>
              <button
                onClick={() => navigate(`/recipe/${importSuccess.recipeId}?fromBrowse=true`)}
                className="px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors"
              >
                View Recipe
              </button>
            </div>
          </div>
        )}

        {/* Duplicate Warning */}
        {duplicateRecipe && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4" data-testid="duplicate-recipe-warning">
            <div className="flex items-start gap-3">
              <AlertCircle size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium text-amber-800">You already have this recipe!</p>
                {duplicateRecipe.title && (
                  <p className="text-sm text-amber-600 mb-3">{duplicateRecipe.title}</p>
                )}
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    onClick={() => navigate(`/recipe/${duplicateRecipe.recipe_id}`)}
                    className="px-4 py-2 bg-amber-600 text-white rounded-lg font-medium hover:bg-amber-700 transition-colors text-sm"
                    data-testid="view-existing-recipe-btn"
                  >
                    View Existing Recipe
                  </button>
                  <button
                    onClick={handleReimport}
                    disabled={reimporting}
                    className="px-4 py-2 bg-white text-amber-700 border border-amber-300 rounded-lg font-medium hover:bg-amber-50 transition-colors text-sm flex items-center gap-2 disabled:opacity-50"
                    data-testid="reimport-recipe-btn"
                  >
                    <RefreshCw size={14} className={reimporting ? 'animate-spin' : ''} />
                    {reimporting ? 'Re-importing...' : 'Re-import'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-4">
            <div className="flex items-start gap-3">
              <AlertCircle size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-red-700">{error}</p>
              </div>
              <button onClick={() => setError('')} className="text-red-400 hover:text-red-600 text-xl">
                ×
              </button>
            </div>
          </div>
        )}

        {/* Main URL Input - Always Prominent */}
        <div className={`bg-white rounded-2xl shadow-md border-2 ${waitingForReturn ? 'border-amber-400 ring-4 ring-amber-100' : 'border-gray-100'} p-5 transition-all`}>
          <h2 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <Globe size={20} className="text-amber-600" />
            Paste Recipe URL
          </h2>
          <form onSubmit={handleImportUrl} className="space-y-3">
            <div className="flex gap-2">
              <div className="flex-1 flex items-center gap-2 bg-gray-100 rounded-xl px-4 py-3">
                <input
                  id="url-input"
                  type="text"
                  value={urlInput}
                  onChange={(e) => setUrlInput(e.target.value)}
                  placeholder="Paste a link from AllRecipes, TikTok, Instagram..."
                  className="flex-1 bg-transparent outline-none text-gray-700"
                  autoFocus={waitingForReturn}
                />
                <button
                  type="button"
                  onClick={handlePasteFromClipboard}
                  className="p-1.5 hover:bg-gray-200 rounded-lg transition-colors text-gray-500"
                  title="Paste from clipboard"
                >
                  <Clipboard size={18} />
                </button>
              </div>
              <button
                type="submit"
                disabled={!urlInput.trim() || loading}
                className="px-6 py-3 bg-amber-600 text-white rounded-xl font-medium hover:bg-amber-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              >
                {importing ? (
                  <>
                    <Loader size={18} className="animate-spin" />
                    <span className="hidden sm:inline">Importing...</span>
                  </>
                ) : (
                  <>
                    <Plus size={18} />
                    <span className="hidden sm:inline">Import</span>
                  </>
                )}
              </button>
            </div>
            <p className="text-xs text-gray-500">
              Paste a direct link to a recipe page (not a search or category page)
            </p>
          </form>
        </div>

        {/* Google Search */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
          <h2 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <Search size={16} />
            Search for Recipes
          </h2>
          <form onSubmit={handleGoogleSearch} className="flex gap-2">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="e.g., chicken parmesan, vegan tacos..."
              className="flex-1 bg-gray-100 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              type="submit"
              disabled={!searchQuery.trim()}
              className="px-5 py-3 bg-blue-500 text-white rounded-xl font-medium hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              <ExternalLink size={16} />
              <span className="hidden sm:inline">Search</span>
            </button>
          </form>
          <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
            <ExternalLink size={10} />
            Opens in new tab - copy the recipe URL and paste above
          </p>
        </div>

        {/* Favorite Sites */}
        {favoriteSites.length > 0 && (
          <div>
            <h2 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <Heart size={16} className="text-red-500 fill-red-500" />
              Favorite Sites
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {favoriteSites.map(site => (
                <div
                  key={site.url}
                  onClick={() => handleSiteClick(site)}
                  className="relative bg-white rounded-xl shadow-sm border border-gray-100 p-4 text-left hover:shadow-md hover:border-amber-200 transition-all group cursor-pointer"
                >
                  <button
                    onClick={(e) => toggleFavorite(site.url, e)}
                    className="absolute top-2 right-2 p-1 rounded-full hover:bg-gray-100"
                  >
                    <Heart size={16} className="text-red-500 fill-red-500" />
                  </button>
                  <span className="text-2xl mb-2 block">{site.icon}</span>
                  <span className="font-medium text-gray-800 text-sm block truncate">{site.name}</span>
                  <span className="text-xs text-gray-400 flex items-center gap-1 mt-1">
                    <ExternalLink size={10} />
                    Opens in new tab
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Custom Sites */}
        {customSites.length > 0 && (
          <div>
            <h2 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              ⭐ My Sites
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {customSites.map(site => (
                <div
                  key={site.url}
                  onClick={() => handleSiteClick(site)}
                  className="relative bg-white rounded-xl shadow-sm border border-amber-200 p-4 text-left hover:shadow-md hover:border-amber-400 transition-all group cursor-pointer"
                >
                  <button
                    onClick={(e) => handleDeleteCustomSite(site.url, e)}
                    className="absolute top-2 right-2 p-1 rounded-full hover:bg-red-100 opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Remove site"
                  >
                    <X size={14} className="text-gray-400 hover:text-red-500" />
                  </button>
                  <span className="text-2xl mb-2 block">{site.icon}</span>
                  <span className="font-medium text-gray-800 text-sm block truncate">{site.name}</span>
                  <span className="text-xs text-gray-400 flex items-center gap-1 mt-1">
                    <ExternalLink size={10} />
                    Opens in new tab
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Social Tip Banner */}
        {showSocialTip && (
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4 mb-4 animate-in fade-in">
            <p className="text-sm text-amber-800 font-medium">
              📱 Find a recipe on TikTok or Instagram, tap <strong>Share → Copy Link</strong>, then paste it in the URL field above!
            </p>
          </div>
        )}

        {/* Social Recipe Sources */}
        {socialSites.length > 0 && (
          <div className="mb-6">
            <h2 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              📱 Social Recipes
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {socialSites.map(site => (
                <div
                  key={site.name}
                  onClick={() => handleSiteClick(site)}
                  className="relative bg-white rounded-xl shadow-sm border border-gray-100 p-4 text-left hover:shadow-md hover:border-amber-200 transition-all group cursor-pointer"
                >
                  <span className="text-2xl mb-2 block">{site.icon}</span>
                  <span className="font-medium text-gray-800 text-sm block truncate">{site.name}</span>
                  <span className="text-xs text-gray-400 flex items-center gap-1 mt-1">
                    Paste link to import
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* All Sites */}
        <div>
          <h2 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <Globe size={16} />
            Popular Recipe Sites
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {otherSites.map(site => (
              <div
                key={site.url}
                onClick={() => handleSiteClick(site)}
                className="relative bg-white rounded-xl shadow-sm border border-gray-100 p-4 text-left hover:shadow-md hover:border-amber-200 transition-all group cursor-pointer"
              >
                <button
                  onClick={(e) => toggleFavorite(site.url, e)}
                  className="absolute top-2 right-2 p-1 rounded-full hover:bg-gray-100 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <Heart size={16} className="text-gray-400 hover:text-red-500" />
                </button>
                <span className="text-2xl mb-2 block">{site.icon}</span>
                <span className="font-medium text-gray-800 text-sm block truncate">{site.name}</span>
                <span className="text-xs text-gray-400 flex items-center gap-1 mt-1">
                  <ExternalLink size={10} />
                  Opens in new tab
                </span>
              </div>
            ))}
            
            {/* Add Custom Site Button */}
            <button
              onClick={() => setShowAddSiteModal(true)}
              className="bg-gray-50 rounded-xl border-2 border-dashed border-gray-200 p-4 text-center hover:border-amber-300 hover:bg-amber-50 transition-all flex flex-col items-center justify-center"
            >
              <Plus size={24} className="text-gray-400 mb-2" />
              <span className="font-medium text-gray-500 text-sm">Add Your Site</span>
            </button>
          </div>
        </div>

        {/* Add Site Modal */}
        {showAddSiteModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Add Recipe Site</h3>
                <button
                  onClick={() => {
                    setShowAddSiteModal(false);
                    setNewSiteName('');
                    setNewSiteUrl('');
                  }}
                  className="p-1 hover:bg-gray-100 rounded-full"
                >
                  <X size={20} className="text-gray-500" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Site Name</label>
                  <input
                    type="text"
                    value={newSiteName}
                    onChange={(e) => setNewSiteName(e.target.value)}
                    placeholder="e.g., Mom's Recipe Blog"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    autoFocus
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Website URL</label>
                  <input
                    type="text"
                    value={newSiteUrl}
                    onChange={(e) => setNewSiteUrl(e.target.value)}
                    placeholder="e.g., momsrecipes.com"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  />
                </div>
                
                <div className="flex gap-3 pt-2">
                  <button
                    onClick={() => {
                      setShowAddSiteModal(false);
                      setNewSiteName('');
                      setNewSiteUrl('');
                    }}
                    className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddCustomSite}
                    disabled={!newSiteName.trim() || !newSiteUrl.trim() || addingSite}
                    className="flex-1 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {addingSite ? <Loader size={16} className="animate-spin" /> : null}
                    Add Site
                  </button>
                </div>
              </div>
              
              <p className="text-xs text-gray-400 mt-4 text-center">
                You can add up to 10 custom sites
              </p>
            </div>
          </div>
        )}

        {/* Bottom padding */}
        <div className="h-8" />
      </div>
    </div>
  );
};

export default BrowseRecipes;
